import * as _ from 'lodash';
import {useMemo, useRef} from 'react';
import {useDeepCompareMemo} from 'use-deep-compare';

import {metricsInExpression} from '../../../util/expr';
import {smoothLines} from '../../../util/plotHelpers/lines';
import {filterNegative} from '../../../util/plotHelpers/plotHelpers';
import {Line} from '../../../util/plotHelpers/types';
import * as Run from '../../../util/runs';
import {useWorkspaceSettingsColorPalette} from '../../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {LineConfig} from '../config/types';
import {usePanelConfigContext} from '../PanelConfigContext';
import {usePanelGroupingSettings} from '../RunsLinePlotContext/usePanelGroupingSettings';
import {BucketedData} from '../types';
import {logVitals} from '../useSamplingLineProfile';
import {
  convertAreaToPercentArea,
  convertLinesToArea,
} from './../../../util/plotHelpers/areaCharts';
import {getBucketSpec} from './../../../util/plotHelpers/buckets/getBucketSpec';
import {markLinesByColor} from './../../../util/plotHelpers/colors';
import * as Time from './../../../util/plotHelpers/time';
import {useRampFlagLineConstructionProfiling} from './../../../util/rampFeatureFlags';
import {getSupplementalMetrics} from './../componentOutliers/getSupplementalMetrics';
import {aggregateLines} from './aggregateLines';
import {buildLines, UseLineBehavior} from './buildLines';
import {evaluateYExpressions} from './evaluateYExpressions';
import {makeAllowedLines} from './lineFilters/filterUnusedLines';
import {SupplementalMetricsByRun} from './types';
import {
  checkIfUsingExpressions,
  checkIfUsingTimeConversion,
  convertLineDataToTimestamp,
  determineXMetrics,
  determineYMetrics,
  evaluateXExpressions,
  preProcessData,
  removeUselessAreaLines,
} from './useLinesUtils';
import {sliceObjectKeys} from './util';

export const useLines = (
  lineConfig: LineConfig,
  data: BucketedData['data']['histories']['data'],
  isBackendAggregated: Record<string, boolean>,
  meta: {
    isInReport: boolean;
    numBucketsByPanelWidth: number;
  },
  debugMode = false // this is a temporary flag to make debugging performance easier
) => {
  const hasData = useRef(false);
  const hasLoggedVitals = useRef(false);

  const lineProfilingEnabled = useRampFlagLineConstructionProfiling(
    lineConfig.entityName
  );

  const {
    groupAgg,
    getGroupKeysByRunsetId,
    isAnyRunsetGrouped,
    isRunsetGrouped,
  } = usePanelGroupingSettings();
  const {expressionKeys, excludeOutliers, xAxisIsDatetime} =
    usePanelConfigContext();

  const lineConfigDeepMemo = useDeepCompareMemo(() => lineConfig, [lineConfig]);
  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  return useMemo(
    function renderUseLinesMemo() {
      /**
       * We only want to instrument the first render of the lines
       */
      if (!hasData.current && data.length > 0) {
        hasData.current = true;
      }

      const _usingExpressions = checkIfUsingExpressions(lineConfig);
      const _usingTimeConversion = checkIfUsingTimeConversion(lineConfig);
      const behavior: UseLineBehavior = {
        canLogVitals: hasData.current && !hasLoggedVitals.current,
        /**
         * Determine if we're need to calculate any expressions - if not we can save some point computation for faster processing times
         */
        usingExpressions: _usingExpressions,
        usesTimeConversion: _usingTimeConversion,
        /**
         * Fallback from backend smoothing if any fallback conditions are met Full fidelity is also a condition here, but it is true by definition as useLines is only called if full fidelity is true
         */
        useBackendSmoothedLineIfPresent:
          !_usingExpressions && !_usingTimeConversion && !isAnyRunsetGrouped,

        /**
         * This is a placeholder. Eventually we're going to be constructing the line data on the backend and we'll figure out how we want to determine this flag value to switch downstream behavior
         */
        useBackendLineData: data.some(
          run => (run.historyLines?.length ?? 0) > 0
        ),
      };

      const t0 = performance.now();

      const xMetrics = determineXMetrics(lineConfig);
      const yMetrics = determineYMetrics(lineConfig);

      preProcessData(data, yMetrics);

      /**
       * Supplemental metrics might be used later for things like evaluating expressions. We make them easy to look up here.
       * Note that `supplementalYExpressionKeys` contains config and summary keys, which are filtered out from `yMetrics` above.
       */
      const supplementalYExpressionKeys = lineConfig.expressions
        ? lineConfig.expressions
            .flatMap(expr => metricsInExpression(expr, false))
            .filter(
              key => key.startsWith('config:') || key.startsWith('summary:')
            )
        : [];
      const supplementalMetricsByRun = data.reduce((acc, run) => {
        acc[run.name] = getSupplementalMetrics(
          [...xMetrics, ...supplementalYExpressionKeys],
          {
            configMetrics: run.config ?? {},
            summaryMetrics: run.summary ?? {},
          }
        );
        return acc;
      }, {} as SupplementalMetricsByRun);

      const t1 = performance.now();
      if (debugMode && t1 - t0 > 1) {
        console.log('Preliminary line work: ', t1 - t0, 'ms');
      }

      let lines = buildLines({
        behavior,
        data,
        debugMode,
        excludeOutliers,
        getGroupKeysByRunsetId,
        groupAgg,
        isRunsetGrouped,
        lineConfig,
        xMetrics,
        yMetrics,
        colorPaletteOption,
      });

      const t2 = performance.now();

      // see notes on handling time plots
      // Readme: frontends/app/src/components/PanelRunsLinePlot/timePlots/readme.md
      // Demo: https://wandb.ai/public-team-private-projects/Time%20plots?nw=36sx43brn2r
      if (xAxisIsDatetime) {
        convertLineDataToTimestamp(lines);
      }

      const t3 = performance.now();

      if (behavior.usesTimeConversion) {
        /**
         * Scale horizontally based on timestep: this looks at the lines,  claculates what kind of scaling factor "seconds", "hours", etc... and then mutates each line
         */
        Time.convertSecondsToTimestep(
          // this is super gross - when you use a method like .flatMap or .flat you get a new array returned
          // if you operate on the elements of that array with something like `forEach` you're still mutating
          // values referenced by the original array, so:
          // ```
          // const lines = [ [{x: 1}, {x: 2} ],[ {x: 1}, {x: 2} ],[ {x: 1}, {x: 2} ] ]
          // lines.flat().forEach(p => p.x = p.x * 2)
          // ```
          // will muatate the data in `lines`
          lines
            .flatMap(lr => lr)
            .flat(2),
          // while zooming we don't want to change our timestep
          lineConfig.zoomTimestep ?? undefined
        );
      }

      const t3a = performance.now();

      /**
       * Grouping on the client is taking all the lines and condensing them down into aggregate lines. This can happen based on:
       * a) group keys from the run selector (values read in from run selector filters)
       * b) group keys from the panel config (values set in the grouping tab)
       * c) aggregating metrics in the panel config
       *
       * Assume we're always going to bucket lines in grouping because if the number of lines gets large the checks to see if they require bucketing get expensive as we have to see if all x-values are in common. Odds are they won't be because of the indeterminancy of bucketing
       */
      // We can't plot any lines that don't have data, nor can we determine the
      // metadata about them (such as min/max/numBuckets/etc)
      const activeLines = lines.filter(function filterActiveLines(l) {
        return l.data && l.data.length > 0;
      });
      const isAnyRunsetAggregated = Object.values(
        isBackendAggregated ?? {}
      ).some(value => !!value);
      if (
        isAnyRunsetGrouped &&
        activeLines.length > 0 &&
        !isAnyRunsetAggregated
      ) {
        /**
         * If we're grouping then group the lines by run keys
         */
        const groupedLines = sliceObjectKeys<Record<string, Line[]>>(
          lineConfig.aggregateMetrics && isAnyRunsetGrouped
            ? _.groupBy(activeLines, line => {
                // is there any real case where a line wouldn't have a run?
                return getGroupKeysByRunsetId(
                  line.run?.runsetInfo?.id ?? ''
                )?.map(gKey => Run.getValue(line.run!, gKey));
              })
            : lineConfig.aggregateMetrics
              ? _.groupBy(activeLines, line => {
                  return `${line.name}`;
                })
              : isAnyRunsetGrouped
                ? _.groupBy(activeLines, line => {
                    return getGroupKeysByRunsetId(
                      line.run?.runsetInfo?.id ?? ''
                    )?.map(gKey =>
                      [
                        line.metricName,
                        Run.getValue(line.run!, gKey),
                        line.run?.runsetInfo?.id ?? '',
                      ].join('-')
                    );
                  })
                : {default: activeLines},
          lineConfig.limit
        );

        const aggLines = Object.values(groupedLines)
          .map(gLines => (gLines ? aggregateLines(gLines, {groupAgg}) : []))
          .flat();

        // filter out to make sure that we don't have any lines corresponding to individual
        // runs that are actually in grouped runsets
        const linesWithoutGrouping = lines.filter(
          line => !isRunsetGrouped(line.run?.runsetInfo?.id ?? '')
        );
        lines = linesWithoutGrouping.concat(aggLines);
      }

      // Note: this is _ugly_. We shouldn't need to copy this data into the primary line in order to populate the
      // overlay legend. But fixing this is a larger refactor with more blast radius than just copying some extra
      // data, so I'm respecting the conventions of the previous implementation for now. This should be refactored
      // in the future when we revisit how we construct the legend and the overlay which has some serious perf issues.
      lines = lines.map(line => {
        if (line.aux) {
          return line;
        }
        const minmaxLine = lines.find(
          l => l.meta.aggregation === 'minmax' && l.uniqueId === line.uniqueId
        );
        return {
          ...line,
          minmaxLine,
        };
      });

      /**
       *  We want to do this sooner rather than later so we're not computing expressions or smoothing on lines which ultimately have no display value
       */
      lines = removeUselessAreaLines(lines);

      const t4 = performance.now();

      if (lineConfig.xExpression) {
        evaluateXExpressions(
          lineConfig.xExpression,
          lines,
          supplementalMetricsByRun
        );
      }

      const t5 = performance.now();

      const hasYExpression =
        lineConfig.expressions && lineConfig.expressions.length > 0;

      const expressionLines = hasYExpression
        ? evaluateYExpressions(
            lines,
            lineConfig.expressions,
            supplementalMetricsByRun
          )
        : [];

      let linesToUse = hasYExpression ? (expressionLines as Line[]) : lines;

      const t6 = performance.now();

      /**
       * TODO: when we smooth lines we mark the meta.category as `smoothed` on the smoothed lines. After we smooth lines we need to mark all the original lines as `aux: true` so they aren't treated as primarily lines. We do this mutation after the smoothing steps here so that we can do it once on `linesToUse` and not
       */
      const isSmoothingActive = lineConfig.smoothingParam > 0;
      if (isSmoothingActive) {
        if (linesToUse.every(l => l.smoothedLineFromBackend != null)) {
          linesToUse = [
            ...linesToUse.map(l => ({...l, aux: true})),
            ...linesToUse.map(l => l.smoothedLineFromBackend!),
          ];
        } else {
          linesToUse = smoothLines(
            // @ts-ignore
            linesToUse,
            lineConfig.smoothingParam,
            lineConfig.smoothingType
          ).flat();
        }
      }
      const t7 = performance.now();

      if (lineConfig.yLogScale) {
        linesToUse = filterNegative(linesToUse);
      }
      let finalLines = makeAllowedLines(linesToUse, {
        excludeOutliers: excludeOutliers ?? 'include-outliers',
        isRunsetGrouped,
        isRunsetAggregated: isBackendAggregated,
        isSmoothingActive: isSmoothingActive,
        showOriginalAfterSmoothing: lineConfig.showOriginalAfterSmoothing,
      });

      const t8 = performance.now();
      const hasAreaChart =
        (lines.length > 0 && lineConfig.plotType === 'stacked-area') ||
        lineConfig.plotType === 'pct-area';
      if (hasAreaChart) {
        // Crazy way of dealing with the fact that the samples don't line up
        // we make some buckets and compute an average value across the buckets
        //
        // We calculate these buckets for all lines together since there are more
        // interactions between the lines in a stacked/area charts than a regular line plot.
        const bucketSpec = getBucketSpec(lines);
        const xToSum: {[key: number]: number} = {};
        // Make it a stacked area chart
        finalLines = convertLinesToArea(finalLines, {
          buckets: bucketSpec,
          expressionKeys: expressionKeys,
          useMedian: false,
          xToSum,
        });

        if (lineConfig.plotType === 'pct-area') {
          convertAreaToPercentArea(finalLines, xToSum);
        }
      }

      markLinesByColor(finalLines as Array<{color: string; mark: string}>);

      const t9 = performance.now();
      if (lineProfilingEnabled && behavior.canLogVitals && t9 - t0 > 1) {
        hasLoggedVitals.current = true;
        const numPoints = data.reduce(
          (acc, run) => acc + run.history.reduce((acc, h) => acc + h.length, 0),
          0
        );
        logVitals(
          {t0, t1, t2, t3, t3a, t4, t5, t6, t7, t8, t9},
          {
            isInReport: meta.isInReport,
            usesAreaChart: hasAreaChart,
            usesBackendLineData: behavior.useBackendLineData,
            usesBackendSmoothing: behavior.useBackendSmoothedLineIfPresent,
            usesGrouping: isAnyRunsetGrouped,
            usesSmoothing: isSmoothingActive,
            usesTimeConversion: behavior.usesTimeConversion,
            usesXExpression: !!lineConfig.xExpression,
            usesYExpressions: hasYExpression,
          },
          {
            numBucketsByPanelWidth: meta.numBucketsByPanelWidth,
            numRuns: data.length,
            numPoints,
          },
          debugMode
        );
      }

      return finalLines;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, lineConfigDeepMemo, excludeOutliers, colorPaletteOption]
  );
};
